/**
 * Funnel session id (rolling 30-min cookie) + coarse analytics context.
 *
 * No PHI here, and deliberately so: the payloads this feeds carry a session id,
 * paths, coarse UTM/device context, and product + checkout ids. The shopper's email
 * reaches the funnel doc only through `postFunnelLink`, which is an explicit call.
 */
import { readAttributionSnapshotForSubmit } from './attributionSession.js'

const COOKIE_NAME = 'hb_funnel_v1'
/** Rolling window: every tracked event re-extends it, so a session ends after 30
 *  minutes of genuine inactivity rather than 30 minutes after it started. */
const MAX_AGE_SECONDS = 30 * 60

function getCookie(name) {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  for (const part of document.cookie.split(';')) {
    const p = part.trim()
    if (p.startsWith(prefix)) {
      try {
        return decodeURIComponent(p.slice(prefix.length))
      } catch {
        return null
      }
    }
  }
  return null
}

function setCookie(name, value, maxAgeSec) {
  if (typeof document === 'undefined') return
  let cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSec}; Path=/; SameSite=Lax`
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    cookie += '; Secure'
  }
  document.cookie = cookie
}

export function clearFunnelSessionCookie() {
  if (typeof document === 'undefined') return
  let cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    cookie += '; Secure'
  }
  document.cookie = cookie
}

/** The backend rejects any sessionId that is not a v4 uuid, so validate before use. */
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function getOrCreateFunnelSessionId() {
  if (typeof window === 'undefined' || typeof crypto === 'undefined' || !crypto.randomUUID) {
    return ''
  }
  const existing = getCookie(COOKIE_NAME)
  if (existing && UUID_V4.test(existing)) return existing
  // A malformed cookie would be rejected by the backend on every event, forever.
  if (existing) clearFunnelSessionCookie()
  const id = crypto.randomUUID()
  setCookie(COOKIE_NAME, id, MAX_AGE_SECONDS)
  return id
}

/** Extend the rolling window on activity. */
export function touchFunnelSession() {
  const id = getCookie(COOKIE_NAME)
  if (id) setCookie(COOKIE_NAME, id, MAX_AGE_SECONDS)
}

function deviceClassFromUa(ua) {
  const s = (ua || '').toLowerCase()
  if (/ipad|tablet|playbook|silk|(android(?!.*mobile))/.test(s)) return 'tablet'
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/.test(s)) return 'mobile'
  return 'desktop'
}

/**
 * Coarse analytics context, sent once per session (the backend sets
 * `contextApplied` and ignores repeats).
 */
export function getStableContext() {
  const snap = readAttributionSnapshotForSubmit()
  const rawUtm =
    snap && snap.rawUtm && typeof snap.rawUtm === 'object'
      ? snap.rawUtm
      : { source: '', medium: '', campaign: '' }
  const utm = {
    source: typeof rawUtm.source === 'string' ? rawUtm.source : '',
    medium: typeof rawUtm.medium === 'string' ? rawUtm.medium : '',
    campaign: typeof rawUtm.campaign === 'string' ? rawUtm.campaign : '',
  }
  if (typeof window === 'undefined') {
    return { utm, landingPath: '', referrer: '', screen: '', deviceClass: 'desktop' }
  }
  const w = window
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  return {
    utm,
    landingPath: w.location.pathname || '',
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    screen: typeof w.screen !== 'undefined' ? `${w.screen.width}x${w.screen.height}` : '',
    deviceClass: deviceClassFromUa(ua),
  }
}
